import { SxProps, Theme } from "@mui/material";
import myPlanTokens, {
  myPlanResponsiveTokens,
} from "screens/Settings/MyPlan/MyPlan.tokens";
import manageSeatsPanelTokens from "./ManageSeatsPanel.tokens";

const t = myPlanTokens;
const r = myPlanResponsiveTokens;
const s = manageSeatsPanelTokens;

/** Card gutter, per breakpoint — matches the plan card it expands inside. */
const gutter = { xs: r.gutter, md: t.space.gutter };

/* ── Panel shell ────────────────────────────────────────────────────────── */

/**
 * The panel expands inside the plan card rather than covering the page, so it
 * carries the card's own gutter and a hairline to separate it from the stats
 * above — the same shell the SMS credits buy panel uses.
 */
export const panelSx: SxProps<Theme> = {
  px: gutter,
  pt: s.panel.paddingTop,
  pb: s.panel.paddingBottom,
  borderTop: `${t.border.hairline} solid ${t.color.headerBorder}`,
  bgcolor: s.panel.bg,
  display: "flex",
  flexDirection: "column",
  gap: s.panel.gap,
};

export const panelHeadingSx: SxProps<Theme> = {
  fontSize: s.panel.headingFontSize,
  fontWeight: s.panel.headingFontWeight,
  color: t.color.ink,
  lineHeight: "normal",
};

/* ── Seat stats ─────────────────────────────────────────────────────────── */

export const statRowSx: SxProps<Theme> = {
  display: "flex",
  gap: s.stat.gap,
};

export const statCellSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "2px",
  px: s.stat.paddingX,
  py: s.stat.paddingY,
  borderRadius: t.radius.control,
  bgcolor: s.stat.bg,
  border: `${t.border.hairline} solid ${s.stat.border}`,
  boxSizing: "border-box",
  textAlign: "center",
};

export const statLabelSx: SxProps<Theme> = {
  fontSize: s.stat.labelFontSize,
  fontWeight: t.weight.strong,
  color: t.color.body,
  letterSpacing: s.stat.labelLetterSpacing,
  textTransform: "uppercase",
  lineHeight: "normal",
};

export const statValueSx: SxProps<Theme> = {
  fontSize: s.stat.valueFontSize,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: "normal",
};

/* ── Stepper row ────────────────────────────────────────────────────────── */

/** Label, the stepper, and the plan's ceiling — one line until it can't be. */
export const stepperRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: s.stepper.gap,
  flexWrap: "wrap",
};

export const stepperLabelSx: SxProps<Theme> = {
  fontSize: s.stepper.labelFontSize,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: "normal",
};

/** The outlined control: [ − | value | + ], hairline-divided, not free buttons. */
export const stepperSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  border: `${t.border.hairline} solid ${t.color.controlBorder}`,
  borderRadius: t.radius.control,
  bgcolor: t.color.surface,
  overflow: "hidden",
  flexShrink: 0,
  "&:focus-within": { borderColor: t.color.ctaBg },
};

export const stepperButtonSx: SxProps<Theme> = {
  width: s.stepper.buttonSize,
  height: s.stepper.buttonSize,
  minWidth: s.stepper.buttonSize,
  p: 0,
  color: t.color.ink,
  fontFamily: "inherit",
  fontSize: s.stepper.controlFontSize,
  fontWeight: t.weight.strong,
  lineHeight: 1,
  borderRadius: 0,
  "&:hover": { bgcolor: s.stepper.hoverBg },
  "&.Mui-disabled": { color: s.stepper.disabled },
};

/** Editable seat count — typed for a big change, stepped for a small one. */
export const stepperInputSx: SxProps<Theme> = {
  width: s.stepper.valueWidth,
  height: s.stepper.buttonSize,
  textAlign: "center",
  fontFamily: "inherit",
  fontSize: s.stepper.valueFontSize,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: 1,
  bgcolor: "transparent",
  border: "none",
  borderLeft: `${t.border.hairline} solid ${s.stepper.divider}`,
  borderRight: `${t.border.hairline} solid ${s.stepper.divider}`,
  p: 0,
  outline: "none",
  MozAppearance: "textfield",
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "&:disabled": { color: s.stepper.disabled },
};

export const stepperHintSx: SxProps<Theme> = {
  fontSize: s.stepper.hintFontSize,
  color: t.color.muted,
  lineHeight: "normal",
};

/* ── Hints ──────────────────────────────────────────────────────────────── */

/** What the chosen count does to people, or why the stepper stopped. */
export const hintSx: SxProps<Theme> = {
  display: "block",
  fontSize: s.stepper.hintFontSize,
  fontWeight: t.weight.regular,
  color: t.color.body,
  lineHeight: 1.5,
};

/** "upgrade for more" — inline text action inside the at-cap hint. */
export const hintLinkSx: SxProps<Theme> = {
  font: "inherit",
  color: t.color.accent,
  fontWeight: t.weight.medium,
  textDecoration: "underline",
  background: "none",
  border: "none",
  p: 0,
  cursor: "pointer",
  "&:hover": { color: t.color.accentHover },
};

/* ── Cost breakdown ─────────────────────────────────────────────────────── */

export const costStackSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: s.cost.rowGap,
};

export const costRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: "12px",
};

export const costLabelSx: SxProps<Theme> = {
  fontSize: s.cost.labelFontSize,
  fontWeight: t.weight.regular,
  color: t.color.body,
  lineHeight: 1.5,
};

export const costValueSx = (isCredit = false): SxProps<Theme> => ({
  flexShrink: 0,
  fontSize: s.cost.valueFontSize,
  fontWeight: t.weight.strong,
  color: isCredit ? s.cost.credit : t.color.ink,
  lineHeight: 1.5,
});

export const costNoteSx: SxProps<Theme> = {
  display: "block",
  fontSize: s.cost.noteFontSize,
  fontWeight: t.weight.regular,
  color: t.color.muted,
  lineHeight: 1.5,
};

/* ── Order bar ──────────────────────────────────────────────────────────── */

export const orderBarSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: s.order.gap,
  flexWrap: "wrap",
  bgcolor: t.color.surface,
  border: `${t.border.hairline} solid ${t.color.cardBorder}`,
  borderRadius: s.order.radius,
  p: s.order.padding,
  boxSizing: "border-box",
};

export const orderLineSx: SxProps<Theme> = {
  fontSize: s.order.lineFontSize,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

/** The renewal sentence — truncates rather than wrapping the bar to two rows. */
export const orderNoteSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  fontSize: s.order.noteFontSize,
  color: t.color.muted,
  lineHeight: "normal",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const orderTotalSx: SxProps<Theme> = {
  ml: { md: "auto" },
  fontSize: s.order.totalFontSize,
  fontWeight: t.weight.heading,
  color: t.color.ink,
  letterSpacing: t.letterSpacing.heading,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

export const orderButtonSx = (enabled: boolean): SxProps<Theme> => ({
  height: s.order.buttonHeight,
  minHeight: s.order.buttonHeight,
  px: s.order.buttonPaddingX,
  py: 0,
  borderRadius: t.radius.control,
  fontSize: t.font.button,
  fontWeight: t.weight.strong,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  flexShrink: 0,
  whiteSpace: "nowrap",
  // The theme writes `background` as a shorthand on the button slots, which
  // beats an `sx` longhand — so the fill has to be pinned a specificity step
  // higher to survive (CLAUDE.md §11.4).
  "&.MuiButton-root": {
    background: enabled ? t.color.ctaBg : s.order.disabledBg,
    color: t.color.onAccent,
    border: "none",
    "&:hover": {
      background: enabled ? t.color.ctaBgHover : s.order.disabledBg,
    },
    "&.Mui-disabled": {
      background: s.order.disabledBg,
      color: t.color.onAccent,
    },
  },
});
